'use client'

import classNames from 'classnames'
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import {
  useComponentVisible,
  useResponsive,
  useTranslations,
} from '@dustin-web/microsite-components/src/hooks'
import { useComparisons } from '@dustin-web/product-components/src/hooks/dataloader/use-comparisons'
import { NumberBadge } from '@dustin-web/microsite-components/src/badge'
import { Drawer } from '@dustin-web/microsite-components/src/drawer'
import { FilterBox } from '@dustin-web/product-components/src/filters/filter-box'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import CompareIcon from '@dustin-web/microsite-components/src/icon/generated/compare'
import Tooltip from '@dustin-web/microsite-components/src/tooltip'
import { DropdownItem, DropdownList } from './components/dropdown'

const getComparsionId = (path: string) => {
  const pageUrl = path.split('/')
  const comparisonUrlIdString = pageUrl[pageUrl.length - 1]
  return comparisonUrlIdString && parseInt(comparisonUrlIdString)
}

const useCompareMenuHooks = () => {
  const { isSmallScreen } = useResponsive()
  const { translate } = useTranslations()
  const { comparisonsData: comparisonsLists, error } = useComparisons()
  const { componentRef, isVisible, setIsVisible } = useComponentVisible<HTMLDivElement>(false)
  const [itemQuantity, setItemQuantity] = useState<number | undefined>(0)
  const [showMobileListFilter, setShowMobileListFilter] = useState(false)
  const showBadge = !!itemQuantity && itemQuantity > 0 && !isVisible
  const pathname = usePathname()
  const comparisonId = getComparsionId(pathname || '/')
  return {
    comparisonId,
    showBadge,
    isSmallScreen,
    translate,
    comparisonsLists,
    error,
    componentRef,
    setIsVisible,
    showMobileListFilter,
    setItemQuantity,
    setShowMobileListFilter,
    itemQuantity,
    isVisible,
  }
}

const CompareMenu = () => {
  const {
    comparisonId,
    showBadge,
    isSmallScreen,
    translate,
    comparisonsLists,
    error,
    componentRef,
    setIsVisible,
    isVisible,
    itemQuantity,
    showMobileListFilter,
    setItemQuantity,
    setShowMobileListFilter,
  } = useCompareMenuHooks()

  useEffect(() => {
    setItemQuantity(
      comparisonsLists?.reduce((prev, cur) => prev + (cur?.items?.length ? cur.items.length : 0), 0)
    )
  }, [setItemQuantity, comparisonsLists])

  useEffect(() => {
    if (isSmallScreen) {
      // When selecting a new list in mobile, close the mobile-filter
      if (showMobileListFilter) {
        setShowMobileListFilter(!showMobileListFilter)
      }
    }
  }, [comparisonId])

  if (error) {
    throw error
  }

  return isSmallScreen ? (
    <Mobile
      setShowMobileListFilter={setShowMobileListFilter}
      showMobileListFilter={showMobileListFilter}
      showBadge={showBadge}
      translate={translate}
      comparisonsLists={comparisonsLists}
    />
  ) : (
    <Desktop
      isVisible={isVisible}
      setIsVisible={setIsVisible}
      translate={translate}
      showBadge={showBadge}
      itemQuantity={itemQuantity}
      componentRef={componentRef}
      comparisonsLists={comparisonsLists}
    />
  )
}

type ComparisonList = {
  id: number
  productListName?: string | null | undefined
  items: { id: number; productErpIdentifier: string }[]
}[]

type MobileProps = {
  setShowMobileListFilter: Dispatch<SetStateAction<boolean>>
  showMobileListFilter: boolean
  showBadge: boolean
  translate: (key: string, options?: any) => any
  comparisonsLists: ComparisonList
}

const Mobile = ({
  setShowMobileListFilter,
  showMobileListFilter,
  showBadge,
  translate,
  comparisonsLists,
}: MobileProps) => (
  <>
    <a
      data-ga-title="compare"
      role="button"
      aria-label="compare"
      tabIndex={0}
      className={classNames(
        'c-mobile-header-menu__secondary-action u-mr-16 u-relative d-block',
        {}
      )}
      onClick={() => setShowMobileListFilter(!showMobileListFilter)}>
      <span className="d-flex align-items-center" data-test-id="compare-count">
        <CompareIcon name="compare" className="i-24" />
      </span>

      <NumberBadge
        show={showBadge}
        maxQuantity={9}
        className="u-bg-red u-absolute u-radius-4 c-mobile-header-menu__secondary-action__badge"
        data-test-id="compare-count"
      />
    </a>
    <Drawer
      visible={showMobileListFilter}
      className={`u-px-0 u-radius-0 u-dustin-scrollbar u-color-black`}
      onClose={() => setShowMobileListFilter(false)}
      position="right"
      theme="white">
      <>
        <FilterBox title={translate('Compare')} icon="compare" withPadding>
          <ul className="ul-stripped u-mb-0 u-fs-14">
            {comparisonsLists
              ?.map((list: any, key: number) => (
                <Link
                  prefetch={false}
                  key={`${list?.productListName}-${key}`}
                  href={`/compare/index/${list.id}`}
                  className={classNames(
                    'a-stripped d-block justify-content-start u-overflow-ellipsis u-w-100 u-mr-4 u-mb-8',
                    {}
                  )}>
                  {`${list.productListName} (${list.items.length})`}
                </Link>
              ))
              .reverse()}
          </ul>
        </FilterBox>
      </>
    </Drawer>
  </>
)

type DesktopProps = {
  isVisible: boolean
  setIsVisible: (visible: boolean) => void
  translate: (key: string, options?: any) => any
  showBadge: boolean
  itemQuantity: number | undefined
  componentRef: React.RefObject<(HTMLDivElement & HTMLElement) | null>
  comparisonsLists: ComparisonList
}
const Desktop = ({
  isVisible,
  setIsVisible,
  translate,
  showBadge,
  itemQuantity,
  componentRef,
  comparisonsLists,
}: DesktopProps) => (
  <li
    className={classNames('c-header__user-item c-header__dropdown u-pr-24 u-line-height-1', {
      'c-header__dropdown--active': isVisible,
    })}>
    <button
      data-ga-title="compare"
      className="c-header__user-btn c-header__dropdown-btn button-stripped"
      onClick={(e: any) => {
        e.preventDefault()
        return isVisible ? setIsVisible(false) : setIsVisible(true)
      }}
      aria-label={translate('Compare')}
      aria-expanded={isVisible ? true : false}>
      <Tooltip
        popoverLayerClassName="c-header__user-btn--tooltip"
        tooltipText={translate('Compare')}>
        <span className="c-header__user-icon only">
          <CompareIcon name="compare" className="i-16" />
        </span>
      </Tooltip>
      <NumberBadge
        show={showBadge}
        quantity={itemQuantity ?? 0}
        maxQuantity={9}
        data-test-id="compare-count"
        className="c-header__badge icon-only u-bg-gray-08 u-color-white"
      />
    </button>
    <div className="c-header__dropdown-box" ref={componentRef}>
      {itemQuantity ? (
        <DropdownList>
          {comparisonsLists
            ?.map((list: any, i: number) => (
              <DropdownItem
                key={i}
                linkHref={`/compare/index/${list?.id}`}
                hasArrowIcon
                title={list?.productListName}
                quantity={list?.items?.length}
              />
            ))
            .reverse()}
        </DropdownList>
      ) : (
        <div className="u-py-8 u-px-8">{translate('Header_YouHaveNoCompareLists')}</div>
      )}
    </div>
  </li>
)

export default CompareMenu
